import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Mic, MicOff, ArrowLeft, ChevronDown, ChevronUp, Paperclip, ThumbsUp, ThumbsDown, Mail, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_BASE } from '../lib/api-base';

const API = API_BASE;

const AVATARS = {
  thinking: '/avatars/hans-thinking.jpg',
  thumbsup: '/avatars/hans-thumbsup.jpg',
  strong: '/avatars/hans-strong.jpg',
  surprised: '/avatars/hans-surprised.jpg',
  wise: '/avatars/hans-wise.jpg',
};

const QUICKSTARTS = [
  'System für mein Haus',
  'PV + Speicher planen',
  'Stringempfehlung',
  'Vorteile eines Systems',
  'Hilfe bei einem Fehler',
  'Montagehilfe',
];

function generateSessionId() {
  return 'hans-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
}

export default function FragHans({
  variant = 'page',
  initialMessage = null,
  initialGreeting = null,
  context = null,
  onClose = null,
}) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [listening, setListening] = useState(false);
  const [extractedState, setExtractedState] = useState({});
  const [ticketStatus, setTicketStatus] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const autoSentRef = useRef(false);
  const greetingShownRef = useRef(false);
  const textareaRef = useRef(null);
  const inputRef = useRef('');
  const contextRef = useRef(context);

  const isTeaser = variant === 'teaser';
  const isFloating = variant === 'floating';
  const isPage = variant === 'page';

  // Mirror current input + context in refs (for speech restart callbacks)
  useEffect(() => { inputRef.current = input; }, [input]);
  useEffect(() => { contextRef.current = context; }, [context]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const newH = Math.min(ta.scrollHeight, 120);
    ta.style.height = newH + 'px';
  }, [input]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial context-aware greeting (local, no LLM call)
  useEffect(() => {
    if (initialGreeting && !greetingShownRef.current && messages.length === 0) {
      greetingShownRef.current = true;
      setMessages([{
        role: 'bot',
        content: initialGreeting,
        avatar: 'strong',
        time: new Date(),
        isGreeting: true,
      }]);
    }
  }, [initialGreeting, messages.length]);

  // Bot-Schutz: einfacher Math-Captcha. Wird nach 1. User-Message getriggert.
  const [challenge, setChallenge] = useState(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [challengeError, setChallengeError] = useState('');
  const [humanVerified, setHumanVerified] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);

  const fetchChallenge = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/chat/challenge`);
      if (r.ok) setChallenge(await r.json());
    } catch (e) { /* silent */ }
  }, []);

  const submitChallenge = useCallback(async () => {
    setChallengeError('');
    if (!challenge?.challengeToken || !challengeAnswer.trim()) return;
    try {
      const r = await fetch(`${API}/api/chat/verify-human`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          challengeToken: challenge.challengeToken,
          answer: challengeAnswer.trim(),
        }),
      });
      if (r.ok) {
        setHumanVerified(true);
        setChallenge(null);
        setChallengeAnswer('');
        if (pendingMessage) {
          const m = pendingMessage;
          setPendingMessage(null);
          // Nochmal senden — diesmal akzeptiert das Backend.
          setTimeout(() => { sendMessageRef.current?.(m); }, 50);
        }
      } else {
        const d = await r.json().catch(() => ({}));
        setChallengeError(d.detail || 'Falsche Antwort, versuch es nochmal.');
        // Frische Aufgabe holen
        fetchChallenge();
        setChallengeAnswer('');
      }
    } catch (e) {
      setChallengeError('Verbindungsfehler — bitte erneut versuchen.');
    }
  }, [challenge, challengeAnswer, sessionId, pendingMessage, fetchChallenge]);

  const sendMessageRef = useRef(null);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text, time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, context: contextRef.current || null }),
      });
      if (res.status === 428) {
        // Captcha erforderlich — Pending-Message speichern, Modal öffnen
        setLoading(false);
        setMessages(prev => prev.slice(0, -1));  // user msg revert
        setPendingMessage(text);
        fetchChallenge();
        return;
      }
      if (!res.ok) throw new Error('Netzwerkfehler');
      const data = await res.json();

      const botMsg = {
        role: 'bot', content: data.response, avatar: data.avatar || 'thinking',
        mode: data.mode, extractedData: data.extractedData,
        readyToCalculate: data.readyToCalculate, assumptions: data.assumptions || [],
        quickReplies: data.quickReplies || [], safetyLevel: data.safetyLevel,
        knowledgeUsed: data.knowledgeUsed || null,
        knowledgeResults: data.knowledgeResults || null,
        ragCitations: data.ragCitations || null,
        messageId: data.messageId || null,
        sessionId: data.sessionId || sessionId,
        userQuestion: text,
        lowInputDisclaimer: data.lowInputDisclaimer || null,
        continueToConfigurator: !!data.continueToConfigurator,
        // Aus sizing_profile (§6–§10, §21, §23–§25)
        profileClass: data.profileClass || null,
        workTime: data.workTime || null,
        systemTendency: data.systemTendency || null,
        branchFormulation: data.branchFormulation || null,
        followUpQuestions: data.followUpQuestions || [],
        missingInfo: data.missingInfo || [],
        peakShaving: !!data.peakShaving,
        wallbox: !!data.wallbox,
        recommendedSystem: data.recommendedSystem || null,
        time: new Date(),
      };
      setMessages(prev => [...prev, botMsg]);

      if (data.extractedData) {
        setExtractedState(prev => ({
          ...prev, ...data.extractedData, _mode: data.mode,
          _profileClass: data.profileClass || prev._profileClass,
          _systemTendency: data.systemTendency || prev._systemTendency,
          _recommendedModel: data.recommendedSystem?.model || prev._recommendedModel,
        }));
      } else if (data.profileClass) {
        setExtractedState(prev => ({
          ...prev,
          _profileClass: data.profileClass,
          _systemTendency: data.systemTendency,
          _recommendedModel: data.recommendedSystem?.model || prev._recommendedModel,
        }));
      } else if (data.recommendedSystem?.model) {
        setExtractedState(prev => ({
          ...prev,
          _recommendedModel: data.recommendedSystem.model,
        }));
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'bot', content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        avatar: 'surprised', time: new Date(), quickReplies: [],
      }]);
    }
    setLoading(false);
  }, [loading, sessionId, fetchChallenge]);

  // Halte den Ref synchron zur aktuellen sendMessage-Closure, sodass die
  // Captcha-Bestätigung die Pending-Message nochmal absetzen kann.
  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  // Auto-send initial message (e.g., from landing teaser)
  useEffect(() => {
    if (initialMessage && !autoSentRef.current && !isTeaser) {
      autoSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [initialMessage, isTeaser, sendMessage]);

  // Cleanup speech on unmount
  // Cleanup media recorder on unmount
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop()); } catch (_) { /* ignore */ }
      try { mediaStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* ignore */ }
    };
  }, []);

  // Voice input via MediaRecorder + backend Whisper transcription.
  // Works reliably on every browser (Desktop Chrome/Firefox/Safari and mobile
  // including Samsung Internet). No Web Speech API — no duplication issues.
  const startRecording = useCallback(async () => {
    if (listening || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const supported = mimeTypes.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || "";
      const rec = supported ? new MediaRecorder(stream, { mimeType: supported }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try {
          const blob = new Blob(audioChunksRef.current, { type: supported || "audio/webm" });
          audioChunksRef.current = [];
          stream.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
          if (blob.size < 500) { setTranscribing(false); return; }
          setTranscribing(true);
          const form = new FormData();
          const ext = supported.includes("mp4") ? "mp4" : supported.includes("ogg") ? "ogg" : "webm";
          form.append("audio", blob, `recording.${ext}`);
          const res = await fetch(`${API}/api/chat/transcribe`, { method: "POST", body: form });
          if (!res.ok) throw new Error("Transkription fehlgeschlagen");
          const data = await res.json();
          const transcript = (data.text || "").trim();
          if (transcript) {
            setInput(prev => {
              const base = (prev || "").trim();
              return base ? base + " " + transcript : transcript;
            });
            setTimeout(() => textareaRef.current?.focus(), 30);
          }
        } catch (err) {
          console.error("Transkription-Fehler", err);
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setListening(true);
    } catch (err) {
      console.error("Mikrofon nicht verfügbar", err);
      setListening(false);
    }
  }, [listening, transcribing]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch (_) { /* ignore */ }
    }
    setListening(false);
  }, []);

  const toggleSpeech = () => {
    if (listening) stopRecording();
    else startRecording();
  };


  const openTicket = useCallback(async (knowledgeEntry, botMsg) => {
    setTicketStatus({ state: 'creating' });
    try {
      const transcript = messages.map(m => ({
        role: m.role,
        content: m.content,
        time: m.time ? new Date(m.time).toISOString() : undefined,
      }));
      const payload = {
        title: `Support-Anfrage: ${knowledgeEntry?.title || 'Allgemein'}`,
        description: botMsg?.content || '',
        source: 'frag_hans',
        knowledgeEntryTitle: knowledgeEntry?.title,
        knowledgeEntryCategory: knowledgeEntry?.category,
        safetyLevel: knowledgeEntry?.safetyLevel || botMsg?.safetyLevel || null,
        productFamily: knowledgeEntry?.productFamily || null,
        wizardContext: contextRef.current || null,
        chatTranscript: transcript,
      };
      const res = await fetch(`${API}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Ticket konnte nicht erstellt werden');
      const ticket = await res.json();
      setTicketStatus({ state: 'created', ticketId: ticket.id });
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `Ticket erfolgreich eröffnet (#${ticket.id.slice(0, 8)}). Unser Support meldet sich bei dir.`,
        avatar: 'thumbsup',
        time: new Date(),
        isTicketConfirmation: true,
      }]);
    } catch (err) {
      setTicketStatus({ state: 'error' });
      setMessages(prev => [...prev, {
        role: 'bot',
        content: 'Das Ticket konnte leider nicht erstellt werden. Bitte versuche es später erneut oder kontaktiere uns direkt.',
        avatar: 'surprised',
        time: new Date(),
      }]);
    }
  }, [messages]);

  const handleDatasheetUpload = useCallback(async (file) => {
    if (!file || uploading) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setMessages(prev => [...prev, {
        role: 'bot',
        content: 'Bitte lade ein PDF-Datenblatt hoch — nur PDF wird für die Extraktion unterstützt.',
        avatar: 'surprised', time: new Date(),
      }]);
      return;
    }
    setUploading(true);
    // User-facing "message" so the upload appears in the conversation
    setMessages(prev => [...prev, {
      role: 'user', content: `📎 Datenblatt hochgeladen: ${file.name}`, time: new Date(),
    }]);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/api/upload/module-datasheet`, { method: 'POST', body: form });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      const variants = Array.isArray(data.variants) ? data.variants : [];
      if (!data.success || variants.length === 0) {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.error || 'Ich konnte aus dem Datenblatt leider keine Moduldaten extrahieren. Bitte nenn mir die Werte (Pmax, Voc, Vmp, Imp, Isc) direkt im Chat.',
          avatar: 'surprised', time: new Date(),
        }]);
      } else {
        const lines = variants.slice(0, 6).map(v =>
          `• ${v.moduleType || 'Variante'}: Pmax=${v.powerWp ?? '?'}Wp · Voc=${v.vocPerModule ?? '?'}V · Vmp=${v.vmpPerModule ?? '?'}V · Imp=${v.nominalCurrentImp ?? '?'}A · Isc=${v.iscPerModule ?? '?'}A · βVoc=${v.betaVocPercent ?? '?'}%/K`
        ).join('\n');
        const summary = `Ich habe ${variants.length} Modulvariante${variants.length === 1 ? '' : 'n'} aus "${data.fileName}" extrahiert:\n\n${lines}\n\nWelche Variante soll ich für deine Auslegung verwenden? Nenn mir einfach die gewünschte Bezeichnung oder Leistungsklasse (z. B. "${variants[0].moduleType || variants[0].powerWp + 'Wp'}").`;
        setMessages(prev => [...prev, {
          role: 'bot', content: summary, avatar: 'thumbsup', time: new Date(),
          assumptions: [`Datenblatt "${data.fileName}" eingelesen — ${variants.length} Variante(n) erkannt`],
        }]);
        // Forward the extracted data to the chat session so Hans picks the right variant
        try {
          await fetch(`${API}/api/chat/message`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              message: `[SYSTEM] Datenblatt-Upload des Nutzers: ${data.fileName}. Extrahierte Varianten (JSON): ${JSON.stringify(variants)}. Frage den Nutzer, welche Variante verwendet werden soll, und übernimm anschließend deren Werte als Defaults für die PV-Auslegung.`,
              context: contextRef.current || null,
            }),
          });
        } catch (_) { /* best-effort */ }
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'bot',
        content: 'Das Datenblatt konnte nicht verarbeitet werden. Bitte versuch es erneut oder nenn mir die Werte direkt.',
        avatar: 'surprised', time: new Date(),
      }]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [uploading, sessionId]);

  const openInConfigurator = (autoCalc = false, skipToResult = false) => {
    const mode = extractedState._mode;
    const profileClass = extractedState._profileClass || null;
    const recommendedModel = extractedState._recommendedModel || null;
    const data = { ...extractedState };
    delete data._mode;
    delete data._profileClass;
    delete data._systemTendency;
    delete data._recommendedModel;
    // Deterministisches Routing pro Button (User-Klarstellung 2026-06-22):
    //   • „Direkt zum Ergebnis (Slider-Modus)" (skipToResult=true)
    //     → IMMER `/konfigurator` (General-Konfigurator mit WhatIf-Slider).
    //     Keine LLM-Stochastik mehr — der Slider-Modus zeigt zuverlässig
    //     dieselbe Seite, egal wie der LLM den Mode klassifiziert hat.
    //   • „Zum detaillierten Konfigurator"/„Berechnung starten" (autoCalc oder
    //     ohne Flags) → `/pv-konfigurator`, sobald PV-Kontext vorhanden ist
    //     (mode='pv' ODER ein PV-Einspeiser mit Leistung > 0 ODER ein
    //     HS-PV-Modell wurde empfohlen). Sonst Wizard `/konfigurator`.
    const hasPvInjector = Array.isArray(data.injectors)
      && data.injectors.some(i => (i?.type || '').toLowerCase() === 'pv'
        && Number(i?.powerKw) > 0);
    const hasPvRecommendation = !!recommendedModel
      && /\bpv\b/i.test(recommendedModel);
    const routePv = !skipToResult
      && (mode === 'pv' || hasPvInjector || hasPvRecommendation);
    if (routePv) {
      // Prefill defaults: Trina Vertex 450W (EES-Standardmodul). Wenn HANS
      // bereits konkrete Werte extrahiert hat, werden diese verwendet — sonst
      // greifen die Defaults, damit `canNext()` im Configurator erfüllt ist
      // und "Direkt zum Ergebnis" / "Berechnung starten" wirklich rechnen.
      const TRINA = { powerWp: 450, voc: 39.5, vmp: 33.5, imp: 11.4, isc: 12.0, beta: -0.30 };
      let entries = (data.entries || []).map((e, i) => {
        const mc = Number(e.moduleCount) || 0;
        const wp = Number(e.powerWpPerModule) || TRINA.powerWp;
        const voc = Number(e.vocPerModule) || TRINA.voc;
        const vmp = Number(e.vmpPerModule) || TRINA.vmp;
        const imp = Number(e.nominalCurrentPerModule) || TRINA.imp;
        const isc = Number(e.iscPerModule) || Number((imp * 1.05).toFixed(2)) || TRINA.isc;
        const beta = e.betaVocPercent != null && e.betaVocPercent !== ''
          ? Number(e.betaVocPercent) : TRINA.beta;
        return {
          id: Date.now() + i,
          stringName: e.name || `String ${i + 1}`,
          fieldName: e.name || `Feld ${i + 1}`,
          orientation: e.orientation || 'sued',
          moduleType: e.moduleType || 'Trina Vertex 450W',
          moduleCount: mc > 0 ? String(mc) : '',
          vocPerModule: String(voc),
          powerWpPerModule: String(wp),
          nominalCurrentPerModule: String(imp),
          betaVocPercent: String(beta),
          vmpPerModule: String(vmp),
          iscPerModule: String(isc),
          minDesignTempC: e.minDesignTempC ? String(e.minDesignTempC) : '',
        };
      });
      // Fallback-Synthese: wenn keine PV-Strings extrahiert wurden (z. B.
      // bei mode='general' mit PV-Injector), bauen wir eine Default-
      // Dachfläche aus der Injector-Leistung — moduleCount via 450W
      // Trina-Vertex-Standardmodul. So bekommt der PV-Konfigurator auch
      // ohne LLM-Stringdaten eine sinnvolle Vorbelegung statt eines
      // leeren Eingabeformulars.
      if (entries.length === 0 && hasPvInjector) {
        const totalPvKw = (data.injectors || [])
          .filter(i => (i?.type || '').toLowerCase() === 'pv')
          .reduce((sum, i) => sum + (Number(i.powerKw) || 0), 0);
        const moduleCount = Math.max(1, Math.round((totalPvKw * 1000) / TRINA.powerWp));
        entries = [{
          id: Date.now(),
          stringName: 'String 1',
          fieldName: 'Dachfläche 1',
          orientation: 'sued',
          moduleType: 'Trina Vertex 450W',
          moduleCount: String(moduleCount),
          vocPerModule: String(TRINA.voc),
          powerWpPerModule: String(TRINA.powerWp),
          nominalCurrentPerModule: String(TRINA.imp),
          betaVocPercent: String(TRINA.beta),
          vmpPerModule: String(TRINA.vmp),
          iscPerModule: String(TRINA.isc),
          minDesignTempC: '',
        }];
      }
      navigate('/pv-konfigurator', {
        state: { prefill: true, mode: data.mode || 'fields', entries, autoCalc, skipToResult, recommendedModel },
      });
      return;
    }
    // Default: general configurator — auch ohne gesetzten _mode (dünne Datenlage)
    const detectedInjectors = Array.isArray(data.injectors) ? data.injectors : [];
    // id anfügen, damit der Konfigurator die Einträge bearbeiten kann.
    // Status normalisieren: die LLM setzt gelegentlich 'new' für eine
    // geplante Anlage. Im Konfigurator ist der Tri-State 'existing' | 'planned'.
    const normalizeStatus = (s) => {
      const v = String(s || '').toLowerCase();
      if (v === 'existing' || v === 'bestehend') return 'existing';
      return 'planned'; // new, planned, null → planned
    };
    const injectorsWithId = detectedInjectors.map((inj, i) => ({
      id: Date.now() + i,
      type: inj.type || 'pv',
      status: normalizeStatus(inj.status),
      powerKw: inj.powerKw ?? '',
      annualYield: inj.annualYield ?? '',
      pvComplexity: inj.pvComplexity || 'standard',
    }));
    const mapped = {
      customerType: data.customerType || null,
      industry: data.industry || null,
      annualConsumption: data.annualConsumptionKwh ?? '',
      consumptionKnown: data.annualConsumptionKwh ? true : null,
      persons: data.persons ?? 3,
      hasEv: !!data.hasEv,
      hasHeatPump: !!data.hasHeatPump,
      scenarioType: data.scenarioType || null,
      backupNeedClass: data.backupNeedClass || 'none',
      peakLoadKnown: data.peakLoadKw ? true : null,
      peakLoadKw: data.peakLoadKw ?? '',
      injectors: injectorsWithId,
      // ‚yes'/‚none' als Tri-State im Konfigurator erwartet
      injectorsExist: injectorsWithId.length > 0 ? 'yes' : null,
    };
    navigate('/konfigurator', {
      state: { prefill: true, data: mapped, autoCalc, skipToResult, profileClass, recommendedModel },
    });
  };

  // Voice input via MediaRecorder + Whisper works on ALL browsers (Desktop and
  // Mobile incl. Samsung Internet). Show mic button whenever MediaRecorder is
  // supported and page is served over a secure context (getUserMedia requires HTTPS).
  const hasSpeech = typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;

  const handleQuickstart = (q) => {
    if (isTeaser) {
      navigate('/frag-hans', { state: { initialMessage: q } });
    } else {
      sendMessage(q);
    }
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!input.trim()) return;
    if (isTeaser) {
      const msg = input;
      setInput('');
      navigate('/frag-hans', { state: { initialMessage: msg } });
    } else {
      sendMessage(input);
    }
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const rootClass = isPage ? 'h-[calc(100vh-56px)]' : 'h-full';

  return (
    <div className={`flex flex-col ${rootClass} bg-white min-w-0 overflow-hidden`} data-testid="frag-hans-chat">
      {/* Bot-Schutz: Mensch-Verifikation */}
      {challenge && !humanVerified && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="captcha-modal">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-semibold text-[#32373c] mb-2 flex items-center gap-2">
              <span aria-hidden>🛡️</span> Kurze Sicherheitsfrage
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Damit HANS nicht von Bots zugespammt wird, brauchen wir kurz
              deine Bestätigung, dass du ein Mensch bist.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rechenaufgabe</div>
              <div className="text-lg font-mono text-[#32373c]">{challenge.question}</div>
            </div>
            <input
              type="number"
              value={challengeAnswer}
              onChange={(e) => setChallengeAnswer(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitChallenge(); }}
              autoFocus
              placeholder="Antwort"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9ec043]/40 focus:border-[#9ec043]"
              data-testid="captcha-answer-input"
            />
            {challengeError && (
              <p className="text-xs text-red-600 mt-2" data-testid="captcha-error">{challengeError}</p>
            )}
            <button
              onClick={submitChallenge}
              disabled={!challengeAnswer.trim()}
              className="mt-3 w-full bg-[#9ec043] hover:bg-[#7ca735] text-white text-sm font-medium py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="captcha-submit"
            >
              Bestätigen & weiter chatten
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      {isPage && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white shrink-0">
          <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-600" data-testid="btn-hans-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src={AVATARS.strong} alt="Hans" className="w-16 h-16 rounded-full object-cover border-2 border-[#9ec043]/40" />
          <div>
            <h2 className="text-sm font-semibold text-[#32373c]">Frag HANS</h2>
            <p className="text-[10px] text-gray-400">EES Beratungsassistent</p>
          </div>
        </div>
      )}
      {isFloating && (
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gradient-to-r from-[#9ec043]/5 to-transparent shrink-0">
          <img src={AVATARS.strong} alt="Hans" className="w-14 h-14 rounded-full object-cover border-2 border-[#9ec043]/40 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-[#32373c]">Frag HANS</h2>
            <p className="text-[10px] text-gray-400 truncate">Ich helfe dir beim Konfigurator</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 shrink-0"
              data-testid="btn-hans-float-close"
              aria-label="Chat schließen"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 py-3 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className={`text-center ${isTeaser ? 'py-1' : 'py-4'}`}>
            {!isTeaser && (
              <img src={AVATARS.strong} alt="Hans" className={`${isFloating ? 'w-28 h-28' : 'w-36 h-36'} rounded-full mx-auto mb-3 border-2 border-[#9ec043]/30 object-cover shadow-sm`} />
            )}
            <p className={`${isTeaser ? 'text-xs mb-2' : 'text-sm mb-3'} text-gray-500`}>
              {isTeaser
                ? 'Beschreiben Sie Ihr Vorhaben — Hans hilft Ihnen weiter.'
                : 'Hallo! Ich bin Hans, Ihr EES-Beratungsassistent. Beschreiben Sie einfach Ihr Vorhaben.'}
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {QUICKSTARTS.map(q => (
                <button
                  key={q}
                  onClick={() => handleQuickstart(q)}
                  className="text-xs px-3 py-1 rounded-full border border-[#9ec043]/30 text-[#9ec043] hover:bg-[#9ec043]/5 transition-colors"
                  data-testid={`chip-${q.slice(0, 10).replace(/\s/g, '-')}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble
            key={i}
            msg={msg}
            sessionId={sessionId}
            allMessages={messages}
            onQuickReply={sendMessage}
            onOpenConfigurator={openInConfigurator}
            onOpenTicket={(knowledgeEntry, botMsg) => openTicket(knowledgeEntry, botMsg)}
          />
        ))}

        {loading && (
          <div className="flex items-start gap-2">
            <img src={AVATARS.thinking} alt="Hans" className="w-14 h-14 rounded-full object-cover border border-gray-200 shrink-0" />
            <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-3 mt-1">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-gray-100 bg-white px-3 py-2">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder={transcribing ? 'Transkribiere…' : (listening ? 'Ich höre zu – tippe erneut um zu stoppen' : 'Nachricht eingeben oder Mikrofon nutzen…')}
            rows={1}
            className="flex-1 min-w-0 py-2 px-4 rounded-2xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-[#9ec043] transition-colors resize-none overflow-y-auto leading-tight"
            style={{ maxHeight: '120px' }}
            data-testid="hans-input"
            disabled={loading}
          />
          {/* PDF datasheet upload (hidden file input + paperclip trigger) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleDatasheetUpload(e.target.files?.[0])}
            data-testid="hans-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors shrink-0 ${uploading ? 'bg-[#9ec043] text-white' : 'text-gray-400 hover:bg-gray-100'}`}
            disabled={uploading || loading}
            title="PV-Datenblatt (PDF) hochladen"
            aria-label="PV-Datenblatt hochladen"
            data-testid="btn-hans-attach"
          >
            {uploading ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Paperclip className="w-4 h-4" />
            )}
          </button>
          {hasSpeech && (
            <button
              type="button"
              onClick={toggleSpeech}
              className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                transcribing ? 'bg-[#9ec043] text-white' : listening ? 'bg-red-500 text-white' : 'text-gray-400 hover:bg-gray-100'
              }`}
              data-testid="btn-hans-mic"
              disabled={transcribing}
              aria-label={listening ? 'Aufnahme stoppen' : 'Sprachaufnahme starten'}
              aria-pressed={listening}
            >
              {listening && (
                <>
                  <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-60" />
                  <span className="absolute inset-[-4px] rounded-full border-2 border-red-400 animate-pulse" />
                </>
              )}
              {transcribing ? (
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : listening ? (
                <MicOff className="w-4 h-4 relative" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-9 h-9 rounded-full bg-[#9ec043] text-white flex items-center justify-center hover:bg-[#8aad38] disabled:opacity-40 transition-colors shrink-0"
            data-testid="btn-hans-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({ msg, sessionId, allMessages, onQuickReply, onOpenConfigurator, onOpenTicket }) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-[#9ec043] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] break-words">
          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
        </div>
      </div>
    );
  }

  const avatar = AVATARS[msg.avatar] || AVATARS.thinking;

  return (
    <div className="flex items-start gap-2 min-w-0">
      <img src={avatar} alt="Hans" className="w-14 h-14 rounded-full object-cover border border-gray-200 shrink-0" />
      <div className="max-w-[85%] min-w-0 space-y-2">
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-2.5 break-words">
          <div className="text-sm text-[#32373c] whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
        </div>

        {msg.safetyLevel === 'QUALIFIED_PERSONNEL_REQUIRED' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
            Diese Prüfung bzw. Maßnahme darf nur durch entsprechend qualifiziertes Fachpersonal durchgeführt werden. Eingriffe am System können sicherheitsrelevant sein.
          </div>
        )}

        {msg.assumptions?.length > 0 && (() => {
          // Strukturiertes Rendern:
          //   „SCHNELLSCHAETZUNG: …" → Block „Schnellschätzung Speicherbedarf"
          //   „BERECHNUNG: …"        → Block „Berechnung"
          //   (sonst Fallback-Block „Verwendete Annahmen")
          const est = [], calc = [], misc = [];
          msg.assumptions.forEach(a => {
            const s = String(a);
            const up = s.toUpperCase();
            if (up.startsWith('SCHNELLSCHAETZUNG:') || up.startsWith('SCHNELLSCHÄTZUNG:')) {
              est.push(s.split(':').slice(1).join(':').trim());
            } else if (up.startsWith('BERECHNUNG:')) {
              calc.push(s.split(':').slice(1).join(':').trim());
            } else {
              misc.push(s);
            }
          });
          const hasStructured = est.length > 0 || calc.length > 0;
          return (
            <div className="space-y-2">
              {est.length > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700"
                     data-testid="hans-quick-estimate">
                  <strong>Schnellschätzung Speicherbedarf:</strong>
                  <ul className="mt-1 space-y-0.5">{est.map((a, i) => <li key={i}>• {a}</li>)}</ul>
                </div>
              )}
              {calc.length > 0 && (
                <div className="bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700"
                     data-testid="hans-calculation">
                  <strong>Berechnung:</strong>
                  <ul className="mt-1 space-y-0.5">{calc.map((a, i) => <li key={i}>• {a}</li>)}</ul>
                </div>
              )}
              {misc.length > 0 && !hasStructured && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-600"
                     data-testid="hans-assumptions">
                  <strong>Verwendete Annahmen:</strong>
                  <ul className="mt-1 space-y-0.5">{misc.map((a, i) => <li key={i}>- {a}</li>)}</ul>
                </div>
              )}
            </div>
          );
        })()}

        {msg.lowInputDisclaimer && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 leading-relaxed"
               data-testid="hans-low-input-disclaimer">
            {msg.lowInputDisclaimer}
          </div>
        )}

        {msg.systemTendency && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-900"
               data-testid="hans-system-tendency">
            <div className="font-semibold text-[10px] uppercase tracking-wide text-emerald-700 mb-0.5">
              Systemrichtung
            </div>
            {msg.systemTendency}
            {msg.peakShaving && (
              <div className="mt-1 text-[11px] text-emerald-800 italic">
                ⚙ Peak-Shaving erkannt — reine PV-kWp-Logik reicht hier nicht; für eine belastbare Auslegung brauchen wir die höchste Viertelstundenleistung, die gewünschte Kappungsgrenze und die typische Dauer der Lastspitzen.
              </div>
            )}
          </div>
        )}

        {msg.recommendedSystem?.model && (
          <div className="bg-white border-2 border-[#9ec043] rounded-lg p-4"
               data-testid="hans-recommended-system">
            <div className="text-[10px] uppercase tracking-wide text-[#9ec043] font-semibold mb-1">
              Empfohlenes System
            </div>
            <div className="text-base font-bold text-[#32373c] mb-2">
              {msg.recommendedSystem.model}
              {msg.recommendedSystem.family && (
                <span className="ml-2 text-xs font-normal text-gray-400">
                  Familie {msg.recommendedSystem.family}
                </span>
              )}
            </div>
            {msg.recommendedSystem.specs && Object.keys(msg.recommendedSystem.specs).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-3">
                {[
                  ['recommendedPvKw', 'Empf. PV-Leistung', 'kWp'],
                  ['maxFeedInKw', 'Max. Einspeisung', 'kW'],
                  ['standardUsableKwh', 'Nutzkapazität', 'kWh'],
                  ['maxDischargeKw', 'Max. Entladung', 'kW'],
                  ['backupContinuousKva', 'Notstrom', 'kVA'],
                  ['backupPeakKva30s', 'Notstrom Peak', 'kVA'],
                ].filter(([k]) => msg.recommendedSystem.specs[k] != null).map(([k, label, unit]) => {
                  const v = msg.recommendedSystem.specs[k];
                  // Floating-point-Artefakte (z.B. 1880.1000000000001) säubern:
                  // Nutzkapazität immer mit 1 Nachkommastelle, andere Werte
                  // mit max. 1 Nachkommastelle (.0 wird ausgeblendet).
                  let display;
                  if (typeof v === 'number') {
                    if (k === 'standardUsableKwh') {
                      display = v.toFixed(1);
                    } else {
                      display = (Math.round(v * 10) / 10).toString();
                    }
                  } else {
                    display = v;
                  }
                  return (
                    <div key={k} className="bg-gray-50 rounded px-2 py-1.5">
                      <div className="text-[10px] text-gray-400">{label}</div>
                      <div className="font-semibold text-[#32373c]">
                        {display} {unit}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {msg.recommendedSystem.sizingExplanation && msg.recommendedSystem.sizingExplanation.length > 0 && (
              <details className="mt-2 group" data-testid="hans-sizing-explanation">
                <summary className="cursor-pointer text-[11px] font-medium text-[#7ca735] hover:text-[#5e8425] inline-flex items-center gap-1 select-none">
                  <span className="transition-transform group-open:rotate-90">▸</span>
                  Warum dieses Modell?
                </summary>
                <ul className="mt-2 space-y-1 pl-3 border-l-2 border-[#9ec043]/30">
                  {msg.recommendedSystem.sizingExplanation.map((line, i) => (
                    <li key={i} className="text-[11px] text-gray-600 leading-relaxed">
                      <span className="text-[#9ec043] mr-1">·</span>{line}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {msg.recommendedSystem.datasheetUrl && (
              <a href={
                msg.recommendedSystem.datasheetUrl.startsWith('http')
                  ? msg.recommendedSystem.datasheetUrl
                  : `${API_BASE}${msg.recommendedSystem.datasheetUrl}`
              }
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-xs text-[#9ec043] hover:text-[#8aad38] hover:underline font-medium"
                 data-testid="hans-datasheet-download">
                <Download className="w-3.5 h-3.5" /> Datenblatt herunterladen
              </a>
            )}
          </div>
        )}

        {msg.branchFormulation && (
          <div className="bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 text-xs text-sky-900 leading-relaxed"
               data-testid="hans-branch-formulation">
            {msg.branchFormulation}
          </div>
        )}

        {msg.followUpQuestions?.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-xs text-indigo-900"
               data-testid="hans-follow-up-questions">
            <div className="font-semibold text-[10px] uppercase tracking-wide text-indigo-700 mb-1">
              Für die finale Auslegung — bitte kurz beantworten
            </div>
            <ul className="space-y-1.5">
              {msg.followUpQuestions.map((q, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-indigo-400 mt-px">◦</span>
                  <button
                    onClick={() => onQuickReply(q.split('(')[0].trim())}
                    className="text-left hover:text-indigo-700 hover:underline"
                    data-testid={`hans-followup-${i}`}
                  >
                    {q}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {msg.missingInfo?.length > 0 && !msg.lowInputDisclaimer && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] text-slate-700"
               data-testid="hans-missing-info">
            <div className="font-semibold text-[10px] uppercase tracking-wide text-slate-500 mb-1">
              Hilfreich für eine belastbare finale Auslegung
            </div>
            <ul className="space-y-0.5">
              {msg.missingInfo.map((m, i) => <li key={i}>• {m}</li>)}
            </ul>
          </div>
        )}

        {msg.knowledgeUsed?.length > 0 && (
          <div className="text-[10px] text-gray-400 italic" data-testid="hans-kb-badge">
            Basierend auf {msg.knowledgeUsed.length} EES-Wissenseintrag{msg.knowledgeUsed.length === 1 ? '' : 'en'}
            <span className="text-gray-300"> · {msg.knowledgeUsed.map(k => k.title).join(', ')}</span>
          </div>
        )}

        {msg.ragCitations?.length > 0 && (
          <div className="space-y-2 mt-1" data-testid="hans-rag-citations">
            <div className="text-[10px] text-emerald-700 font-medium uppercase tracking-wide">
              Aus dem EES-Handbuch
            </div>
            {msg.ragCitations.map((c, i) => (
              <div key={i} className="bg-emerald-50/60 border border-emerald-100 rounded-lg px-3 py-2"
                   data-testid={`rag-citation-${i}`}>
                <div className="text-[11px] font-semibold text-emerald-900 flex items-center gap-1.5">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span className="truncate">{c.title}</span>
                  {c.pageIndex != null && <span className="text-emerald-700/70 font-normal">· Seite {c.pageIndex + 1}</span>}
                  <span className="ml-auto text-[10px] text-emerald-700/60">Relevanz {(c.score * 100).toFixed(0)}%</span>
                </div>
                {c.snippet && (
                  <p className="text-[11px] text-emerald-900/80 mt-1 leading-relaxed">{c.snippet}</p>
                )}
                {c.images?.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {c.images.map((img, j) => (
                      <a key={j} href={`${API}${img.url}`} target="_blank" rel="noreferrer"
                         className="block border border-emerald-200 rounded-md overflow-hidden bg-white hover:border-emerald-400 transition-colors"
                         data-testid={`rag-img-${i}-${j}`}>
                        <img src={`${API}${img.url}`} alt={`Abbildung aus ${c.title}`}
                             className="w-28 h-28 object-contain" loading="lazy" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Troubleshooting: expandable knowledge cards + "Ticket eröffnen" */}
        {msg.mode === 'troubleshooting' && msg.knowledgeResults?.length > 0 && (
          <div className="space-y-1.5" data-testid="hans-trouble-cards">
            {msg.knowledgeResults.slice(0, 3).map((k, i) => (
              <KnowledgeCard
                key={i}
                entry={k}
                onOpenTicket={() => onOpenTicket?.(k, msg)}
              />
            ))}
          </div>
        )}

        {msg.readyToCalculate && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onOpenConfigurator(true, false)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-8" data-testid="btn-hans-calculate">Berechnung starten</Button>
            <Button variant="outline" onClick={() => onOpenConfigurator(false, true)} className="text-xs h-8 text-gray-500" data-testid="btn-hans-open-config">Direkt zum Ergebnis</Button>
            <Button variant="outline" onClick={() => onQuickReply('Daten präzisieren')} className="text-xs h-8 text-gray-500">Daten anpassen</Button>
            <ForwardToEesButton sessionId={sessionId} allMessages={allMessages} currentMsg={msg} />
          </div>
        )}

        {msg.continueToConfigurator && !msg.readyToCalculate && (
          <div className="flex flex-wrap gap-2" data-testid="hans-continue-cta">
            <Button onClick={() => onOpenConfigurator(false, false)}
                    className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-8"
                    data-testid="btn-hans-continue-config">
              Zum detaillierten Konfigurator
            </Button>
            <Button variant="outline" onClick={() => onOpenConfigurator(false, true)}
                    className="text-xs h-8 text-gray-500"
                    data-testid="btn-hans-skip-to-result">
              Direkt zum Ergebnis (Slider-Modus)
            </Button>
            <ForwardToEesButton sessionId={sessionId} allMessages={allMessages} currentMsg={msg} />
          </div>
        )}

        {msg.extractedData && Object.keys(msg.extractedData).length > 0 && (
          <button onClick={() => setDetailsOpen(!detailsOpen)} className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600">
            {detailsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Erkannte Daten
          </button>
        )}
        {detailsOpen && msg.extractedData && (
          <div className="bg-gray-50 rounded-lg p-2 text-[10px] text-gray-500 font-mono whitespace-pre-wrap break-all">
            {JSON.stringify(msg.extractedData, null, 2)}
          </div>
        )}

        {msg.quickReplies?.length > 0 && !msg.readyToCalculate && (
          <div className="flex flex-wrap gap-1.5">
            {msg.quickReplies.map((qr, i) => (
              <button key={i} onClick={() => onQuickReply(qr)} className="text-xs px-3 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">{qr}</button>
            ))}
          </div>
        )}

        {msg.messageId && <FeedbackBar msg={msg} />}
      </div>
    </div>
  );
}

// localStorage-Key für die optionale Identifikation auf /frag-hans.
// Wenn Nutzer einmal Name/E-Mail eingeben, gilt das für alle weiteren
// Bewertungen im selben Browser — sie müssen es nicht erneut eintippen.
const HANS_IDENT_KEY = 'hans_fb_identity_v1';
function _loadIdent() {
  try { return JSON.parse(localStorage.getItem(HANS_IDENT_KEY) || '{}'); }
  catch { return {}; }
}
function _saveIdent(name, email) {
  try {
    const clean = {
      name: (name || '').trim(),
      email: (email || '').trim().toLowerCase(),
    };
    if (clean.name || clean.email) localStorage.setItem(HANS_IDENT_KEY, JSON.stringify(clean));
    else localStorage.removeItem(HANS_IDENT_KEY);
  } catch { /* ignore quota errors */ }
}

function FeedbackBar({ msg }) {
  const [state, setState] = useState(null); // 'sending' | 'up' | 'down'
  // Up-/Down-Modus mit Identifikations-Feldern. Beide Pfade nutzen
  // dieselbe Form, damit die UX konsistent ist (Name+optional E-Mail
  // oben, Kommentar nur bei Daumen runter).
  const [pendingScore, setPendingScore] = useState(null); // 1 | -1 | null
  const [comment, setComment] = useState('');
  const initial = _loadIdent();
  const [name, setName] = useState(initial.name || '');
  const [email, setEmail] = useState(initial.email || '');
  const API = API_BASE;

  const send = async (score, extraComment = '', custName = '', custEmail = '') => {
    setState('sending');
    try {
      await fetch(`${API}/api/chat/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: msg.sessionId || null,
          messageId: msg.messageId,
          score,
          comment: extraComment,
          customerName: custName || null,
          customerEmail: custEmail || null,
          question: msg.userQuestion || '',
          answer: msg.content,
          citations: (msg.ragCitations || []).map(c => ({
            docId: c.docId, title: c.title, pageIndex: c.pageIndex, score: c.score,
          })),
        }),
      });
      _saveIdent(custName, custEmail);
      setState(score > 0 ? 'up' : 'down');
    } catch { setState(null); }
  };

  if (state === 'up' || state === 'down') {
    return (
      <div className="text-[10px] text-gray-400 italic" data-testid="hans-feedback-sent">
        Danke {name ? `${name}, ` : ''}für dein Feedback {state === 'up' ? '👍' : '👎'} — damit verbessern wir Hans fortlaufend.
      </div>
    );
  }

  // Aktive Form (Daumen geklickt, Identifikation + ggf. Kommentar)
  if (pendingScore !== null) {
    const isDown = pendingScore < 0;
    return (
      <div className="flex flex-col gap-1.5 bg-gray-50 border border-gray-200 rounded-lg p-2"
           data-testid="hans-feedback-form">
        <div className="text-[10px] text-gray-500 flex items-center gap-1">
          {isDown ? <ThumbsDown className="w-3 h-3 text-red-500" /> : <ThumbsUp className="w-3 h-3 text-emerald-600" />}
          <span>
            {isDown
              ? 'Was hat dir an der Antwort gefehlt? (optional, hilft uns Hans zu verbessern)'
              : 'Cool, dass es geholfen hat! Optional: identifiziere dich, wenn du Antwort vom EES-Team willst.'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <input type="text" value={name} onChange={e => setName(e.target.value)}
                 placeholder="Dein Name (optional)"
                 className="text-xs h-7 px-2 rounded border border-gray-200 bg-white focus:outline-none focus:border-[#9ec043]"
                 data-testid="input-feedback-name" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                 placeholder="E-Mail (optional)"
                 className="text-xs h-7 px-2 rounded border border-gray-200 bg-white focus:outline-none focus:border-[#9ec043]"
                 data-testid="input-feedback-email" />
        </div>
        {isDown && (
          <input type="text" value={comment} onChange={e => setComment(e.target.value)}
                 placeholder="Was fehlte oder war falsch?"
                 className="text-xs h-7 px-2 rounded border border-gray-200 bg-white focus:outline-none focus:border-[#9ec043]"
                 data-testid="input-feedback-comment"
                 onKeyDown={e => e.key === 'Enter' && send(pendingScore, comment, name, email)} />
        )}
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => setPendingScore(null)}
                  className="text-[10px] text-gray-400 hover:text-gray-700"
                  data-testid="btn-feedback-cancel">
            Abbrechen
          </button>
          <button onClick={() => send(pendingScore, isDown ? comment : '', name, email)}
                  className="text-xs font-semibold text-white bg-[#9ec043] hover:bg-[#7a9c34] rounded px-3 py-1"
                  data-testid="btn-feedback-send">
            Senden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5" data-testid="hans-feedback-bar">
      <span className="text-[10px] text-gray-400">War die Antwort hilfreich?</span>
      <button onClick={() => setPendingScore(1)} disabled={state === 'sending'}
              className="p-1 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
              data-testid="btn-feedback-up" title="Hilfreich">
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button onClick={() => setPendingScore(-1)} disabled={state === 'sending'}
              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              data-testid="btn-feedback-down" title="Nicht hilfreich">
        <ThumbsDown className="w-3 h-3" />
      </button>
    </div>
  );
}

function formatMessage(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

function KnowledgeCard({ entry, onOpenTicket }) {
  const [open, setOpen] = useState(false);
  const preview = (entry.content || '').slice(0, 120);
  const isSafetyCritical = entry.safetyLevel === 'QUALIFIED_PERSONNEL_REQUIRED';

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-testid="hans-kb-card">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-xs font-medium text-[#32373c] flex-1 min-w-0 truncate">{entry.title}</span>
        {entry.category && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0 uppercase tracking-wide">{entry.category}</span>
        )}
        {isSafetyCritical && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">Fachpersonal</span>
        )}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
      </button>
      {!open && preview && (
        <p className="px-3 pb-2 text-[11px] text-gray-500 leading-relaxed">{preview}{entry.content?.length > 120 ? '…' : ''}</p>
      )}
      {open && (
        <div className="px-3 pb-3 pt-0 space-y-2">
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{entry.content}</p>
          {isSafetyCritical && (
            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
              Diese Maßnahme darf nur von qualifiziertem Fachpersonal durchgeführt werden.
            </p>
          )}
          <button
            type="button"
            onClick={onOpenTicket}
            className="text-[11px] px-3 py-1 rounded-full border border-[#9ec043]/40 text-[#9ec043] hover:bg-[#9ec043]/5 transition-colors"
            data-testid="btn-hans-open-ticket"
          >
            Ticket eröffnen
          </button>
        </div>
      )}
    </div>
  );
}



function ForwardToEesButton({ sessionId, allMessages, currentMsg }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!email && !phone) {
      setErr('Bitte E-Mail oder Telefonnummer angeben.');
      return;
    }
    setSending(true); setErr(null);
    try {
      const payload = {
        sessionId,
        customerName: name || null,
        customerEmail: email || null,
        customerPhone: phone || null,
        note: note || null,
        messages: (allMessages || []).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content })),
        lastProductRecommendation: extractRecommendation(currentMsg),
        lastExtractedData: currentMsg?.extractedData || null,
      };
      const res = await fetch(`${API}/api/chat/forward-to-ees`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) { setDone(true); }
      else {
        const d = await res.json().catch(() => ({}));
        setErr(d.detail || 'Senden fehlgeschlagen');
      }
    } catch (e) {
      setErr(e.message || 'Netzwerkfehler');
    }
    setSending(false);
  };

  return (
    <>
      <Button variant="outline"
              onClick={() => setOpen(true)}
              className="text-xs h-8 border-[#9ec043]/40 text-[#7ca735] hover:bg-[#9ec043]/5"
              data-testid="btn-hans-forward-ees">
        <Mail className="w-3.5 h-3.5 mr-1" /> An EES weiterleiten
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !sending && setOpen(false)}
             data-testid="hans-forward-dialog-backdrop">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5"
               onClick={e => e.stopPropagation()}
               data-testid="hans-forward-dialog">
            {done ? (
              <div className="text-center py-4" data-testid="hans-forward-success">
                <div className="text-[#9ec043] text-5xl mb-2">✓</div>
                <h3 className="text-lg font-bold text-[#32373c] mb-1">Weitergeleitet!</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Dein EES-Berater hat deine Anfrage bekommen und meldet sich in Kürze bei dir.
                </p>
                <Button onClick={() => setOpen(false)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white">
                  Schließen
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-[#32373c] mb-1">An EES weiterleiten</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Dein Gesprächsverlauf mit Hans wird an unser Vertriebsteam gesendet. Ein EES-Berater meldet sich bei dir — keine weiteren Formulare nötig.
                </p>
                <div className="space-y-2">
                  <input type="text" value={name} onChange={e => setName(e.target.value)}
                         placeholder="Dein Name (optional)"
                         className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
                         data-testid="hans-forward-name" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                         placeholder="E-Mail-Adresse"
                         className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
                         data-testid="hans-forward-email" />
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                         placeholder="Telefon (optional)"
                         className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
                         data-testid="hans-forward-phone" />
                  <textarea value={note} onChange={e => setNote(e.target.value)}
                            rows={3}
                            placeholder="Zusätzliche Nachricht (optional)"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
                            data-testid="hans-forward-note" />
                  {err && <div className="text-xs text-red-600">{err}</div>}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}
                            className="text-xs h-9" data-testid="hans-forward-cancel">
                      Abbrechen
                    </Button>
                    <Button onClick={submit} disabled={sending || (!email && !phone)}
                            className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-9"
                            data-testid="hans-forward-submit">
                      {sending ? 'Sende …' : 'An EES senden'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function extractRecommendation(msg) {
  const text = msg?.content || '';
  const patterns = [/\b(GS HV \d{1,3})\b/, /\b(GS \d{1,2})\b/, /\b(HS \d{1,2} PV duo)\b/, /\b(HS \d{1,2} PV)\b/, /\b(HS \d{1,2})\b/];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) return m[1];
  }
  return null;
}
